import React, {
    useContext,
    FC,
    useState,
    useRef,
    useLayoutEffect,
    MutableRefObject,
    SetStateAction, ReactElement
} from 'react';
import ImageComponent from "../utilities/ImageComponent";
import IframeVideo from "../utilities/VideoUtils/IframeVideo";
import {
    getFormattedEventData,
    getTimezoneAbbr,
    getVideoElementTypeFromItemType,
} from "../../utils/CleversiteUtilities";
import Html5Video from "../utilities/VideoUtils/Html5Video";
import moment from "moment";
import {capitalize, getPlatform, getSocialIcon} from "../../utils/StringUtilities";
import styles from "../cleverfeed/blocks/styles/ListItemGroup.module.scss";
import FlatIcon from "../utilities/FlatIcon";
import FontAwesome from "../utilities/FontAwesome";
import {observer} from "mobx-react";
import {modalStore, StoreContext} from "../../stores/StoreLoader";
import classNames from 'classnames';
import {getContentItemIcon} from "../blocks/TerracedGridTheme/FilesFoldersBlock/fileUtilities";
import ClickableLink from "../utilities/ClickableLink";
import SocialMarkdown from "../utilities/SocialMarkdown";
import {sourceTypesEnum} from "../../utils/SourceUtils";
import {
    IReactiveCourseWorkContentItem,
    IReactiveEventContentItem,
    IReactiveNewsContentItem, isCoursework
} from "../../stores/SchoolFeedStore";
import {isDescendantOrEq} from "../../utils/DataUtilities";
import {
    ContentItemReadMore,
    getCourseWorkIcons,
    handleContentItemClick
} from "../blocks/SchoolBlocks/ContentItemBlock/ContentItemBlock";
import {ContentItemDate} from "../blocks/TerracedGridTheme/NewsBlock/NewsItem";
import WatsonApi from "../../backends/WatsonApi";

export function isEvent(item: EventContentItem | ContentItem): item is EventContentItem {
    return (item as EventContentItem).is_event === true;
}

export function isMedia(item: MediaContentItem | ContentItem): item is MediaContentItem {
    return (item as MediaContentItem).is_media === true;
}

export function isFile(item: FileContentItem | ContentItem): item is FileContentItem {
    return (item as FileContentItem).is_file === true;
}

export function isFolder(item: FileContentItem | ContentItem): item is FileContentItem {
    return (item as FolderContentItem).is_folder === true;
}

export function isNews(item: NewsContentItem | ContentItem): item is NewsContentItem {
    return (item as NewsContentItem).is_news === true;
}


export function Media (props) {
    let media: JSX.Element | null = <></>;
    if (isMedia(props.itemObj) && props.itemObj.json_data.videos && props.itemObj.json_data.videos.videos_list) {
        const type = getVideoElementTypeFromItemType(props.itemObj.type);
        media = type === "iframe" ?
            <IframeVideo embedLink={props.itemObj.json_data.videos.videos_list[0].raw_url}/> :
            <Html5Video embedLink={props.itemObj.json_data.videos.videos_list[0].raw_url}/>;
    } else if (isEvent(props.itemObj) && props.itemObj.json_data.event) {
        const start = moment(props.itemObj.json_data.event.start);
        const end = moment(props.itemObj.json_data.event.end);

        const startMonth = start.format("MMM");
        const startDay = start.format("D");

        if (props.itemObj.json_data.event.multi_day) {
            const endMonth = end.format("MMM");
            const endDay = end.format("D");

            if (startMonth === endMonth) {
                media = <div className={props.style}>
                    <span>{startMonth}</span>
                    <span>{startDay} - {endDay}</span>
                </div>
            } else {
                media = <div className={props.style}>
                    <span>{startMonth} {startDay} -</span>
                    <span>{endMonth} {endDay}</span>
                </div>
            }
        } else {
            media = <div className={props.style}>
                <span>{startMonth}</span>
                <span>{startDay}</span>
            </div>
        }
    }
    else if (isCoursework(props.itemObj)) {
        media = getCourseWorkIcons(props.itemObj);
    }
    else if (props.itemObj.image) {
        const imageIsSourceImage = props.itemObj.image === props.itemObj.source?.account_image || props.itemObj.image === props.itemObj.source?.account_image?.replace("_normal", "");
        media = <ImageComponent data-smallwidth={imageIsSourceImage}
                                src={props.itemObj.image}
                                alt={props.itemObj.altText}
                                onClick={props.onClick}/>
    }

    return media;
}

export function ItemDetails (props) {
    let itemDetails;
    let formatted;

    if (props.itemObj.is_event && (props.itemObj as IReactiveEventContentItem).json_data.event) {
        formatted = getFormattedEventData(props.itemObj, undefined, "h:mma");
    }
    if (isEvent(props.itemObj)) {
        itemDetails = <div className={props.style}>
            {formatted?.timeStr && <div>
                <FlatIcon name={'flaticon-clock'}/>&nbsp;<span>{formatted.timeStr}</span>&nbsp;<span>{formatted.timeStr !== "All Day" && getTimezoneAbbr()}</span>
            </div>}
            {props.itemObj.json_data.event.location && <div role={"button"} onClick={() => modalStore.addModal({
                type: "events",
                itemObj: props.itemObj,
                id: props.itemObj.id,
            })}>
                <FlatIcon name={'flaticon-map'}/> {props.itemObj.json_data.event.location}
            </div>}
        </div>
    } else if (isFile(props.itemObj)) {
        const iconUrl = getContentItemIcon(props.itemObj);
        itemDetails = <div className={props.style}>
            <ClickableLink href={props.itemObj.json_data.file.view_link}>
                <img src={iconUrl} alt={"File Icon"} />
                Click to View File
            </ClickableLink>
        </div>
    }

    return itemDetails;
}

const handleOpenNewsItemModal = itemObj => {
    if (/drive.google.com/.test(itemObj.third_party_url)) {
        modalStore.addModal({
            type: "file-preview",
            id: itemObj.id,
            itemObj: { // need to spoof a file item here
                ...itemObj,
                json_data: {
                    file: {
                        view_link: itemObj.third_party_url,
                    },
                },
            },
        })
    } else if (isNews(itemObj)) { // use test type when creating new source to not allow open modal
        modalStore.addModal({
            type: "news",
            id: itemObj.id,
            itemObj: itemObj,
        })
    } else if (isEvent(itemObj)) {
        modalStore.addModal({
            type: "events",
            itemObj: itemObj,
            id: itemObj.id,
        });
    }
};

const Item = observer((props: {
    itemObj: IReactiveNewsContentItem | IReactiveEventContentItem | IReactiveCourseWorkContentItem,
    allowCuration: boolean,
    deleteItem: (item: IReactiveEventContentItem | IReactiveNewsContentItem) => void,
    unpublishItem: (item: IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveCourseWorkContentItem) => void,
}) => {
    const {userStore, organizationStore, sidebarStore} = useContext(StoreContext);
    const itemRef: MutableRefObject<any> = useRef(null);

    const [shortened, setShortened] = useState(false);

    const handleKeydown = (evt) => {
        if (evt.code === "Enter") {
            const element: HTMLElement = document.activeElement as HTMLElement;
            element.click();
        }
    }

    useLayoutEffect(() => {
        setShortened(itemRef?.current?.clientHeight > 350);
    }, []);

    const itemClassName = classNames({
        [styles.item]: true,
        [styles.itemHidden]: !props.itemObj.published,
        [styles.itemShortened]: shortened,
    })

    let sourceIconLink;
    const platform = getPlatform(props.itemObj.type);
    if (props.itemObj.third_party_url && ![sourceTypesEnum.ICAL, sourceTypesEnum.RSS].includes(platform)) {
        const iconClassName = classNames({
            [styles[`sourceIcon${capitalize(platform)}`]]: true,
            [styles.sourceIcon]: true,
        })
        sourceIconLink = <ClickableLink className={iconClassName} href={props.itemObj.third_party_url} title={props.itemObj.source.account_title}>
            <FontAwesome name={getSocialIcon(platform, true)} />
        </ClickableLink>
    }

    const currentUserCanModifyAtCurrentOrganization = props.itemObj.organization && isDescendantOrEq(props.itemObj.organization.path, organizationStore.currentOrganization.path);

    return <div className={itemClassName} ref={itemRef} onClick={(e) => {
        e.stopPropagation();
        handleContentItemClick(props.itemObj)}
    }>
        {userStore.editor && props.allowCuration && <div className={styles.curationContainer}>
            {props.itemObj.is_custom && <button onClick={async (e) => {
                e.stopPropagation()
                sidebarStore.setSidebar({
                    view: "SchoolFeedPost",
                    contentItem: props.itemObj,
                    handleSave: async (contentItem, organization) => {
                        const result = await organizationStore.fetchClient("organizations", "organizations_content_items_partial_update", {
                            organization_pk: props.itemObj.organization_id,
                            id: props.itemObj.id,
                            data: contentItem,
                        });

                        const item = JSON.parse(result.data);
                        // expanded organization is not returned from an update, and it might have changed
                        props.itemObj.update({...item, organization})
                    },
                    handleDelete: async (contentItem) => {
                        await props.deleteItem(contentItem)
                    }
                })
            }}>
                <FlatIcon name='flaticon-pen'/>
            </button>}
            <button onClick={e => {
                e.stopPropagation();
                props.unpublishItem(props.itemObj)
            }} title={currentUserCanModifyAtCurrentOrganization ? "Toggle Item Visibility" : "You do not have permission to toggle item visibility at current SchoolFeed level."} disabled={!currentUserCanModifyAtCurrentOrganization}>
                <FontAwesome ariaHidden={true} prefix={'far'} name={props.itemObj.published ? 'fa-eye-slash' : 'fa-eye'}/>
            </button>
        </div>}
        <div className={styles.header}>
            {props.itemObj.title && <div className={styles.title}><SocialMarkdown platform={platform} text={props.itemObj.title} /></div>}
            {isNews(props.itemObj) && <div className={styles.dateContainer}>
                <ContentItemDate contentItem={props.itemObj} />
            </div>}
            {isCoursework(props.itemObj) && props.itemObj.json_data?.coursework?.dueDate &&
                <div className = {styles.date} style={{fontWeight: 'bold'}}>
                    Due Date: {moment(props.itemObj.json_data?.coursework?.dueDate).format('MMM D, YYYY')}
                </div>}
            {isEvent(props.itemObj) && <ItemDetails itemObj={props.itemObj} style={styles.dateLocationContainer}/>}
            {props.itemObj.description && <div className={styles.description} tabIndex={shortened ? 0 : -1} onKeyDown={shortened ? handleKeydown : undefined}>{sourceIconLink}<SocialMarkdown text={props.itemObj.description} platform={platform} /></div>}
            {isFile(props.itemObj) && <ItemDetails itemObj={props.itemObj} style={styles.dateLocationContainer}/>}
        </div>
        {shortened && <ContentItemReadMore contentItem={props.itemObj} />}
    </div>
});

const ListItemGroup: FC<{
    items: Array<IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveCourseWorkContentItem>,
    updateItems: SetStateAction<any>,
    allowCuration: boolean,
    showMedia: boolean,
}> = observer(props => {

    const firstItem = props.items[0];

    async function deleteItem(item: IReactiveNewsContentItem | IReactiveEventContentItem) {
        const client = await WatsonApi();
        await client.apis.organizations.organizations_content_items_delete({
            id: item.id,
            organization_pk: item.organization_id,
        });
        props.updateItems(v => v.filter(n => n.id !== item.id));
    }

    async function unpublishItem(item: IReactiveNewsContentItem | IReactiveEventContentItem | IReactiveCourseWorkContentItem) {
        const client = await WatsonApi();
        const result = await client.apis.organizations.organizations_content_items_partial_update({
            id: item.id,
            organization_pk: item.organization_id,
            data: {"user_published": !item.published},
        });
        item.update(result.obj);
    }

    return <div className={styles.groupContainer}>
        {props.showMedia && <Media itemObj={firstItem} style={styles.dateMedia} onClick={(e) => {
            e.stopPropagation();
            handleContentItemClick(firstItem) }
        }/>}
        <div className={styles.contentContainer}>
            {props.items.map(item => <Item
                allowCuration={props.allowCuration}
                key={item.id}
                itemObj={item}
                deleteItem={deleteItem}
                unpublishItem={unpublishItem}
            />)}
            {firstItem.organization && <div className={styles.organizationTitle}>
                {firstItem.organization.title}
            </div>}
        </div>
    </div>
})

export default ListItemGroup
